import moment from "moment";
import { createSelector } from "reselect";
import i18n from "~/i18n";
import { descSortCompare } from "~/utils/utils";
import { getAllAvailabilityTypes } from "./selector.availabilityTypes.jsx";
import { getFirstWeekDay, getTimeWorkdays } from "./selector.configOptions.jsx";
import { getDaysOffArray } from "./selector.daysOff.jsx";
import { getUserById } from "./selector.users.jsx";
import { getWeeklyUserCapacityArrayById } from "./selector.weeklyWorkerCapacity.js";

const getAllAvailabilityRecords = (state) => state.availability_records;

export const getAvailabilityRecordsArray = createSelector(getAllAvailabilityRecords, (records) =>
  Object.values(records)
);

const makeGetUserAvailabilityRecords = (userId) =>
  createSelector(getAvailabilityRecordsArray, (records) =>
    records.filter((record) => {
      return record.user_id === userId;
    })
  );

const getRecordById = createSelector(
  getAllAvailabilityRecords,
  (state, id) => id,
  (records, id) => records[id]
);

export const getIsPassedRecord = createSelector(
  (state, { id }) => getRecordById(state, id),
  (state, { timestamp }) => timestamp,
  (record, timestamp) => (record?.end_date || 0) < timestamp
);

export const makeUserAvailabilityRecordsSelector = (userId, timestamp) => {
  return createSelector(getAllAvailabilityTypes, getAvailabilityRecordsArray, (types, records) => {
    return records
      .filter((record) => {
        return record.user_id === userId && record.end_date >= timestamp;
      })
      .sort((a, b) => descSortCompare(b.end_date, a.end_date))
      .reverse()
      .map((record) => ({
        id: record.id,
        name: types[`${record.availability_type_id}`]?.name || i18n.t("Unknown Type Name"),
        startDate: record.start_date,
        endDate: record.end_date,
        duration: record.duration,
      }));
  });
};

export const makeUserAvailabilityRecordsForSelectDateSelector = (userId, timestamp, type = "user_day_off") =>
  createSelector(
    makeUserAvailabilityRecordsSelector(userId, timestamp),
    (state) => getUserById(state, userId),
    (records, user) =>
      records.map((record) => {
        return {
          id: record.id,
          title: `${user.get("display_name")}: ${record.name}`,
          from: record.startDate,
          to: record.endDate,
          repeating: false,
          type,
        };
      })
  );

export const makeYearsWithRecords = (userId) =>
  createSelector(makeGetUserAvailabilityRecords(userId), (records) => {
    const years = [];

    Object.values(records).forEach((record) => {
      let year = moment.unix(record.end_date).format("YYYY");

      if (!years.includes(year)) {
        years.push(year);
      }
    });

    return years.sort().reverse();
  });

export const makeGetUserRecordsByYear = (userId, year) =>
  createSelector(getAllAvailabilityTypes, makeGetUserAvailabilityRecords(userId), (types, records) =>
    records
      .filter((record) => {
        return (
          moment.unix(record.end_date).format("YYYY") === year || moment.unix(record.start_date).format("YYYY") === year
        );
      })
      .sort((a, b) => descSortCompare(b.end_date, a.end_date))
      .reverse()
      .map((record) => ({
        id: record.id,
        name: types[`${record.availability_type_id}`]?.name || i18n.t("Unknown Type Name"),
        startDate: record.start_date,
        endDate: record.end_date,
        duration: record.duration,
      }))
  );

export const makeIsDayOffSelector = (userId, timestamp) =>
  createSelector(
    makeGetUserAvailabilityRecords(userId),
    getWeeklyUserCapacityArrayById(userId),
    getFirstWeekDay,
    (records, weeklyCapacity, firstWeekDay) => {
      const capacity = weeklyCapacity;

      const reverseCapacity = [...weeklyCapacity];
      const sunday = reverseCapacity.shift();
      reverseCapacity.push(sunday);

      const dayOfWeek = moment.unix(timestamp).utc().weekday();

      return (
        !!records.find((record) => record.start_date <= timestamp && record.end_date >= timestamp) ||
        (firstWeekDay ? reverseCapacity[dayOfWeek] === 0 : capacity[dayOfWeek] === 0)
      );
    }
  );

export const makeGetSumOfDaysByTypePerYearSelector = (userId, year) =>
  createSelector(
    getAllAvailabilityTypes,
    makeGetUserAvailabilityRecords(userId),
    getTimeWorkdays,
    getDaysOffArray,
    (types, records, timeWorkdays, daysOff) => {
      year = Number(year);
      const result = {};
      const daysOffList = [];

      daysOff.forEach((dayOff) => {
        const dayOffStart = moment.unix(dayOff.start_date).utc();
        const dayOffEnd = moment.unix(dayOff.end_date).utc();
        const repeatYearly = dayOff.repeat_yearly || false;

        if (repeatYearly) {
          if (dayOffStart.isSame(dayOffEnd, "year")) {
            dayOffStart.year(year);
            dayOffEnd.year(year);
          } else {
            dayOffStart.year(year - 1);
            dayOffEnd.year(year);
          }
        }

        if (dayOffStart.year() === year || dayOffEnd.year() === year) {
          do {
            daysOffList.push(dayOffStart.format("YYYY-MM-DD"));
            dayOffStart.add(1, "days");
          } while (dayOffStart.isSameOrBefore(dayOffEnd));
        }
      });

      records = records.filter((record) => {
        return (
          moment.unix(record.start_date).utc().year() === year || moment.unix(record.end_date).utc().year() === year
        );
      });

      records.forEach((record) => {
        const type = types[`${record.availability_type_id}`];
        const typeName = type?.name;

        if (typeName) {
          if (!result[typeName]) {
            result[typeName] = 0;
          }

          const start = moment.unix(record.start_date).utc();
          const end = moment.unix(record.end_date).utc();

          if (start.year() < year) {
            start.year(year).startOf("year");
          }

          if (end.year() > year) {
            end.year(year).endOf("year");
          }

          do {
            if (timeWorkdays.includes(start.day()) && !daysOffList.includes(start.format("YYYY-MM-DD"))) {
              result[typeName]++;
            }
            start.add(1, "days");
          } while (start.isSameOrBefore(end));
        }
      });

      return result;
    }
  );

export const makeGetTotalSumOfDaysPerYearSelector = (userId, year) => {
  return createSelector(makeGetSumOfDaysByTypePerYearSelector(userId, year), (mapTypesCount) => {
    return Object.values(mapTypesCount).reduce((a, b) => a + b, 0);
  });
};

export const makeGetRecordsCountByTypeSelector = (userId, year) => {
  return createSelector(makeGetSumOfDaysByTypePerYearSelector(userId, year), (mapTypesCount) => {
    return Object.keys(mapTypesCount)
      .sort((a, b) => {
        return a.toLowerCase().localeCompare(b.toLowerCase());
      })
      .filter((key) => mapTypesCount[key] > 0)
      .map((key) => {
        return {
          name: key,
          count: mapTypesCount[key],
        };
      });
  });
};

export const makeGetAvailabilityRecordsByTimespanSelector = (start, end) => {
  return createSelector(getAvailabilityRecordsArray, (records) => {
    return records.filter((record) => record.start_date <= end && record.end_date >= start);
  });
};
